import React, { useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Typography,
  Stack,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiEdit } from 'react-icons/fi';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import useAgreementSelector from '../../store/Slice/Agreement/AgreementSelector';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getAgreementView } from '../../store/Slice/Agreement/AgreementSlice';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: '#f8fafc'
  },
  '&:hover': {
    backgroundColor: 'rgba(2, 132, 199, 0.04) !important'
  },
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: 13,
  padding: '12px 16px',
  color: '#475569',
  '&.MuiTableCell-head': {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    color: '#0284c7',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.5px'
  }
}));

function InfoItem({ label, value }) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: '#64748b',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          display: 'block',
          mb: 0.5
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: '#1e293b'
        }}
      >
        {value || 'N/A'}
      </Typography>
    </Box>
  );
}

function ViewAgreement() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { rowId, record } = location?.state || {};

  const { LoginData } = useLoginSelector();
  const { AgreementViewData } = useAgreementSelector();

  const getAgreementViewApi = useCallback(() => {
    if (!LoginData || !rowId) return;
    const credential = {
      aggrementId: rowId,
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getAgreementView(credential));
  }, [LoginData, rowId, dispatch]);

  useEffect(() => {
    getAgreementViewApi();
  }, [getAgreementViewApi]);

  const isActiveStatus =
    AgreementViewData?.agreementIsActive !== undefined
      ? AgreementViewData.agreementIsActive
      : record?.aggrementIsActive;

  return (
    <Page title="Partner Management System || View Agreement">
      <Container maxWidth="xl">
        <Box pt={3} pb={4} mb={4}>
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
            sx={{ mb: 3 }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/agreement')}
                sx={{
                  borderRadius: '8px',
                  borderColor: '#cbd5e1',
                  color: '#64748b',
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 2,
                  py: 0.8,
                  bgcolor: '#fff',
                  '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
                }}
              >
                Back
              </Button>
              <PageTitle info="View Agreement" />
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              {AgreementViewData && AgreementViewData?.agreementIsActive !== false && (
                <Button
                  variant="contained"
                  startIcon={<FiEdit size={16} />}
                  onClick={() =>
                    navigate(`/agreement/edit`, { state: { rowId: rowId, record: record } })
                  }
                  sx={{
                    bgcolor: '#0284c7',
                    color: '#fff',
                    borderRadius: '8px',
                    px: 2.5,
                    py: 0.8,
                    fontSize: 13,
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 2px 8px rgba(2, 132, 199, 0.25)',
                    '&:hover': { bgcolor: '#0369a1' }
                  }}
                >
                  Edit Agreement
                </Button>
              )}
              <Box>
                <BrudCrumbs />
              </Box>
            </Stack>
          </Stack>

          <Stack spacing={3}>
            {/* Card 1: Bank Details */}
            {AgreementViewData?.bankDetails && (
              <Card
                sx={{
                  p: 3.5,
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 3 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '10px',
                        bgcolor: 'rgba(19, 81, 157, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#13519d'
                      }}
                    >
                      <AccountBalanceIcon fontSize="small" />
                    </Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                      Bank Details
                    </Typography>
                  </Stack>

                  {isActiveStatus !== undefined && (
                    <Label
                      variant="ghost"
                      color={
                        (isActiveStatus === null && 'warning') ||
                        (isActiveStatus === false && 'error') ||
                        'success'
                      }
                      sx={{
                        textTransform: 'capitalize',
                        fontWeight: 600,
                        borderRadius: '6px',
                        px: 1.5,
                        py: 0.5
                      }}
                    >
                      {isActiveStatus === null
                        ? 'Incomplete'
                        : isActiveStatus === true
                        ? 'Active'
                        : 'Inactive'}
                    </Label>
                  )}
                </Stack>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoItem label="Bank Name" value={AgreementViewData.bankDetails.bankName} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoItem label="PAN Number" value={AgreementViewData.bankDetails.panNumber} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoItem label="GSTIN" value={AgreementViewData.bankDetails.gstIn} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoItem
                      label="Commission Structure"
                      value={AgreementViewData.bankDetails.bankCommissionTypeName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoItem
                      label="Turnover Benefit"
                      value={
                        AgreementViewData.bankDetails.turnoverBenefit
                          ? AgreementViewData.bankDetails.turnoverBenefit
                              .toString()
                              .charAt(0)
                              .toUpperCase() +
                            AgreementViewData.bankDetails.turnoverBenefit.toString().slice(1)
                          : 'N/A'
                      }
                    />
                  </Grid>
                </Grid>
              </Card>
            )}

            {/* Card 2: Branch Details */}
            {AgreementViewData?.branchDetails?.length > 0 && (
              <Card
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    p: 2.5,
                    bgcolor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: 'rgba(2, 132, 199, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#0284c7'
                      }}
                    >
                      <StorefrontIcon fontSize="small" />
                    </Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                      Branch Details
                    </Typography>
                  </Stack>
                  <Label variant="ghost" color="info" sx={{ fontWeight: 600, borderRadius: '6px' }}>
                    {AgreementViewData.branchDetails.length} Branches
                  </Label>
                </Box>

                <Scrollbar>
                  <TableContainer sx={{ minWidth: 600 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Branch Name</StyledTableCell>
                          <StyledTableCell>Branch Address</StyledTableCell>
                          <StyledTableCell>Contact Person</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {AgreementViewData.branchDetails.map((item, index) => (
                          <StyledTableRow key={index}>
                            <StyledTableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                              {item.branchName || 'N/A'}
                            </StyledTableCell>
                            <StyledTableCell>{item.branchAddress || 'N/A'}</StyledTableCell>
                            <StyledTableCell>
                              {item.contactPerson?.contactPersonName || 'N/A'}
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Scrollbar>
              </Card>
            )}

            {/* Card 3: Commission Details */}
            {AgreementViewData?.commissionDetails?.length > 0 && (
              <Card
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    p: 2.5,
                    bgcolor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: 'rgba(16, 185, 129, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#10b981'
                      }}
                    >
                      <ReceiptLongIcon fontSize="small" />
                    </Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                      Commission Details
                    </Typography>
                  </Stack>
                  <Label
                    variant="ghost"
                    color="success"
                    sx={{ fontWeight: 600, borderRadius: '6px' }}
                  >
                    {AgreementViewData.commissionDetails.length} Slabs
                  </Label>
                </Box>

                <Scrollbar>
                  <TableContainer sx={{ minWidth: 600 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Loan Type</StyledTableCell>
                          <StyledTableCell>Branch</StyledTableCell>
                          <StyledTableCell>Commission %</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {AgreementViewData.commissionDetails.map((item, index) => (
                          <StyledTableRow key={index}>
                            <StyledTableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                              {item.loanTypeName || 'N/A'}
                            </StyledTableCell>
                            <StyledTableCell>{item.branchName || 'N/A'}</StyledTableCell>
                            <StyledTableCell>
                              <Label
                                variant="ghost"
                                color="primary"
                                sx={{ fontWeight: 600, borderRadius: '6px' }}
                              >
                                {item.aggrementCommissionPercentage !== undefined &&
                                item.aggrementCommissionPercentage !== null
                                  ? `${item.aggrementCommissionPercentage}%`
                                  : 'N/A'}
                              </Label>
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Scrollbar>
              </Card>
            )}

            {/* Card 4: Billing Details */}
            {AgreementViewData?.billingDetails && (
              <Card
                sx={{
                  p: 3.5,
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '10px',
                      bgcolor: 'rgba(245, 158, 11, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#f59e0b'
                    }}
                  >
                    <AccountBalanceWalletIcon fontSize="small" />
                  </Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                    Billing Details
                  </Typography>
                </Stack>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoItem
                      label="Invoice Mode"
                      value={AgreementViewData.billingDetails.invoiceMode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoItem
                      label="Billing Cycle Day"
                      value={AgreementViewData.billingDetails.billingCycleDay}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoItem
                      label="Payment Terms"
                      value={
                        AgreementViewData.billingDetails.paymentTermsDays
                          ? `${AgreementViewData.billingDetails.paymentTermsDays} Days`
                          : 'N/A'
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoItem
                      label="GST %"
                      value={
                        AgreementViewData.billingDetails.gstPercent !== undefined &&
                        AgreementViewData.billingDetails.gstPercent !== null
                          ? `${AgreementViewData.billingDetails.gstPercent}%`
                          : 'N/A'
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoItem
                      label="TDS %"
                      value={
                        AgreementViewData.billingDetails.tdsPercent !== undefined &&
                        AgreementViewData.billingDetails.tdsPercent !== null
                          ? `${AgreementViewData.billingDetails.tdsPercent}%`
                          : 'N/A'
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoItem
                      label="Chargeback Enabled"
                      value={AgreementViewData.billingDetails.chargebackEnabled ? 'Yes' : 'No'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoItem
                      label="Chargeback Period"
                      value={
                        AgreementViewData.billingDetails.chargebackPeriodDays
                          ? `${AgreementViewData.billingDetails.chargebackPeriodDays} Days`
                          : 'N/A'
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoItem
                      label="Chargeback Amount"
                      value={
                        AgreementViewData.billingDetails.chargebackAmount !== undefined &&
                        AgreementViewData.billingDetails.chargebackAmount !== null
                          ? `₹${AgreementViewData.billingDetails.chargebackAmount}`
                          : 'N/A'
                      }
                    />
                  </Grid>
                </Grid>
              </Card>
            )}

            {/* Card 5: Turnover Benefit */}
            {AgreementViewData?.turnoverBenefits?.length > 0 && (
              <Card
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    p: 2.5,
                    bgcolor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: 'rgba(99, 102, 241, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6366f1'
                      }}
                    >
                      <TrendingUpIcon fontSize="small" />
                    </Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                      Turnover Benefit
                    </Typography>
                  </Stack>
                  <Label
                    variant="ghost"
                    color="warning"
                    sx={{ fontWeight: 600, borderRadius: '6px' }}
                  >
                    {AgreementViewData.turnoverBenefits.length} Benefits
                  </Label>
                </Box>

                <Scrollbar>
                  <TableContainer sx={{ minWidth: 600 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Loan Type</StyledTableCell>
                          <StyledTableCell>Branch</StyledTableCell>
                          <StyledTableCell>Duration</StyledTableCell>
                          <StyledTableCell>Commission %</StyledTableCell>
                          <StyledTableCell>Unit</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {AgreementViewData.turnoverBenefits.map((item, index) => (
                          <StyledTableRow key={index}>
                            <StyledTableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                              {item.loanTypeName || 'N/A'}
                            </StyledTableCell>
                            <StyledTableCell>{item.branchName || 'N/A'}</StyledTableCell>
                            <StyledTableCell>{item.duration || 'N/A'}</StyledTableCell>
                            <StyledTableCell>
                              <Label
                                variant="ghost"
                                color="primary"
                                sx={{ fontWeight: 600, borderRadius: '6px' }}
                              >
                                {item.aggrementCommissionPercentage !== undefined &&
                                item.aggrementCommissionPercentage !== null
                                  ? `${item.aggrementCommissionPercentage}%`
                                  : 'N/A'}
                              </Label>
                            </StyledTableCell>
                            <StyledTableCell>{item.unit || 'N/A'}</StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Scrollbar>
              </Card>
            )}
          </Stack>
        </Box>
      </Container>
    </Page>
  );
}

export default ViewAgreement;
